import { useMemo } from 'react'

import analyticsJson from './fixtures/analytics.json'
import campaignsJson from './fixtures/campaigns.json'
import enumsJson from './fixtures/enums.json'
import upliftJson from './fixtures/w3/uplift.json'
import copyW4Json from './fixtures/w4/copy.json'
import generatedJson from './fixtures/w4/generated.json'
import reasoningJson from './fixtures/w4/reasoning.json'
import scopeJson from './fixtures/w4/scope.json'
import { accountCtr, initialAds, validateAdSums } from './ads'
import { deriveAutopilotDefaults, isAutopilotEligible } from './autopilot'
import { initialRisks, initialViolations } from './compliance'
import { initialCreatives } from './creatives'
import { selectCreativeLibrary } from './creativeSelectors'
import { formatMultiple, formatUsd } from './format'
import { useDemoStore } from './store'
import { aiQuota, cpaTargets, todayFixture, trendFixture } from './today'
import type {
  AnalyticsLens,
  AnalyticsRangeKey,
  AnalyticsSummaryItem,
  AnalyticsView,
  AccountUplift,
  AutopilotState,
  Campaign,
  HandledTodo,
  MonitorKpis,
  Optimization,
  Product,
  ResultDisplay,
  TabKey,
  TodoSegments,
  TrackedCampaign,
  Uplift,
  Generated,
  Reasoning,
  ScopeDimension,
} from './types'
import type {
  Ad,
  AiProgress,
  ComplianceCounts,
  CreativeFilter,
  CreativeLibraryItem,
  CreativeStats,
  LibraryCreative,
  ProductLensRow,
  ProductLensView,
  Risk,
  TodayMetrics,
  TodoItem,
  TrendMini,
  Violation,
} from './types'

interface CampaignFixture {
  products: Product[]
  campaigns: Campaign[]
}

interface EnumFixture {
  resultMetric: Record<string, string>
  attentionReason: Record<string, string>
  tabs: Array<{ key: TabKey; statuses: string[] | null }>
}

interface RawSummary {
  n: number
  text?: string
  dynamic: boolean
  template?: string
  linkText?: string
  linkTo?: string
}

interface AnalyticsSlice {
  kpis: AnalyticsView['kpis']
  chart: Record<string, unknown>
  summary: RawSummary[]
  table: AnalyticsView['table']
}

interface AnalyticsRange {
  key: AnalyticsRangeKey
  label: string
  dateLabel: string
  days: string[]
  sales: AnalyticsSlice
  awareness: AnalyticsSlice
}

interface AnalyticsFixture {
  ranges: Record<AnalyticsRangeKey, AnalyticsRange>
}

const campaignFixture = campaignsJson as unknown as CampaignFixture
const enumFixture = enumsJson as unknown as EnumFixture
const analyticsFixture = analyticsJson as unknown as AnalyticsFixture
const productsById = new Map(campaignFixture.products.map((product) => [product.id, product]))

export { selectCreativeLibrary } from './creativeSelectors'

export function selectMonitorKpis(campaigns: Campaign[]): MonitorKpis {
  return campaigns.reduce<MonitorKpis>((result, campaign) => ({
    running: result.running + (campaign.status === 'running' ? 1 : 0),
    spendMonth: result.spendMonth + campaign.spendMonth,
    orders: result.orders + campaign.orders,
    reach: result.reach + (campaign.resultMetric === 'reach' ? campaign.reach : 0),
  }), { running: 0, spendMonth: 0, orders: 0, reach: 0 })
}

export function selectTabCounts(campaigns: Campaign[]): Record<TabKey, number> {
  return Object.fromEntries(enumFixture.tabs.map((tab) => [
    tab.key,
    tab.statuses === null ? campaigns.length : campaigns.filter((campaign) => tab.statuses?.includes(campaign.status)).length,
  ])) as Record<TabKey, number>
}

export function selectFilteredCampaigns(campaigns: Campaign[], tab: TabKey, query: string): Campaign[] {
  const statuses = enumFixture.tabs.find((item) => item.key === tab)?.statuses ?? null
  const normalizedQuery = query.trim().toLocaleLowerCase('zh-Hans')
  return campaigns.filter((campaign) => {
    if (statuses && !statuses.includes(campaign.status)) return false
    if (!normalizedQuery) return true
    const productName = productsById.get(campaign.productId)?.name ?? ''
    return `${productName}\n${campaign.adName}`.toLocaleLowerCase('zh-Hans').includes(normalizedQuery)
  })
}

export function selectAnalyticsView(campaigns: Campaign[], rangeKey: AnalyticsRangeKey, lens: AnalyticsLens, compare: boolean): AnalyticsView {
  const range = analyticsFixture.ranges[rangeKey]
  const slice = range[lens]
  const invalidLinks = campaigns.filter((campaign) => campaign.status === 'attention' && campaign.attentionReason === 'product_link_invalid').length
  const summary = slice.summary.reduce<AnalyticsSummaryItem[]>((items, item) => {
    if (item.dynamic && invalidLinks === 0) return items
    const text = item.dynamic
      ? (item.template ?? '').replace('{n}', String(invalidLinks))
      : (item.text ?? '')
    items.push({
      n: items.length + 1,
      text,
      dynamic: item.dynamic,
      linkText: item.dynamic ? item.linkText : undefined,
      linkTo: item.dynamic ? item.linkTo : undefined,
    })
    return items
  }, [])
  return {
    rangeKey,
    rangeLabel: range.label,
    dateLabel: range.dateLabel,
    days: range.days,
    lens,
    compare,
    kpis: slice.kpis,
    chart: slice.chart,
    summary,
    table: slice.table,
  }
}

export function selectResultDisplay(campaign: Campaign): ResultDisplay {
  const percent = campaign.result.target === 0 ? 0 : Math.round((campaign.result.current / campaign.result.target) * 100)
  const footItems: string[] = []
  if (campaign.resultMetric === 'orders') {
    if (campaign.metrics.cpa !== null) footItems.push(`CPA ${formatUsd(campaign.metrics.cpa, 2)}`)
    if (campaign.metrics.roas !== null) footItems.push(`ROAS ${formatMultiple(campaign.metrics.roas)}`)
  } else if (campaign.resultMetric === 'reach' && campaign.metrics.cpm !== null) {
    footItems.push(`CPM ${formatUsd(campaign.metrics.cpm, 2)}`)
  }
  return {
    metricLabel: enumFixture.resultMetric[campaign.resultMetric] ?? campaign.resultMetric,
    current: campaign.result.current,
    target: campaign.result.target,
    percent,
    done: percent >= 100,
    footItems,
    warn: campaign.attentionReason ? (enumFixture.attentionReason[campaign.attentionReason] ?? campaign.attentionReason) : null,
  }
}

export function useMonitorKpis(): MonitorKpis {
  const campaigns = useDemoStore((state) => state.campaigns)
  return useMemo(() => selectMonitorKpis(campaigns), [campaigns])
}

export function useTabCounts(): Record<TabKey, number> {
  const campaigns = useDemoStore((state) => state.campaigns)
  return useMemo(() => selectTabCounts(campaigns), [campaigns])
}

export function useFilteredCampaigns(tab: TabKey, query: string): Campaign[] {
  const campaigns = useDemoStore((state) => state.campaigns)
  return useMemo(() => selectFilteredCampaigns(campaigns, tab, query), [campaigns, query, tab])
}

export function useCampaign(id: string): Campaign | undefined {
  return useDemoStore((state) => state.campaigns.find((campaign) => campaign.id === id))
}

export function useAnalyticsView(rangeKey: AnalyticsRangeKey, lens: AnalyticsLens, compare: boolean): AnalyticsView {
  const campaigns = useDemoStore((state) => state.campaigns)
  return useMemo(() => selectAnalyticsView(campaigns, rangeKey, lens, compare), [campaigns, compare, lens, rangeKey])
}

export function useResultDisplay(campaign: Campaign): ResultDisplay {
  return useMemo(() => selectResultDisplay(campaign), [campaign])
}

export interface FixtureCheck {
  label: string
  pass: boolean
  actual: string
  expected: string
}

function check(label: string, actual: unknown, expected: unknown): FixtureCheck {
  const actualText = JSON.stringify(actual)
  const expectedText = JSON.stringify(expected)
  return { label, pass: actualText === expectedText, actual: actualText, expected: expectedText }
}

export function runFixtureSelfCheck(): FixtureCheck[] {
  const campaigns = campaignFixture.campaigns
  const analytics = selectAnalyticsView(campaigns, '7d', 'sales', true)
  return [
    check('监控 KPI', selectMonitorKpis(campaigns), { running: 3, spendMonth: 18420, orders: 342, reach: 512254 }),
    check('页签计数', selectTabCounts(campaigns), { all: 6, running: 3, attention: 1, completed: 2 }),
    check('7 天销售 KPI', analytics.kpis.map((item) => item.value), [18420, 342, 52464, 2.85]),
    check('7 天任务标签', analytics.table.map((item) => item.label), ['轻量保温杯｜商品销售｜美国', '夏季销售增长', '新品认知提升', '独立站引流']),
    check('7 天任务花费', analytics.table.map((item) => item.spend), [6420, 5000, 4800, 2200]),
    check('R78 四区间双口径视角一致', analyticsViewsConsistent(campaigns), true),
  ]
}

function activeViolations(violations: Violation[]): Violation[] {
  return violations.filter((violation) => violation.state !== 'resolved')
}

function activeRisks(risks: Risk[]): Risk[] {
  return risks.filter((risk) => risk.state === 'open')
}

export function selectAttachedCreatives(
  creatives: LibraryCreative[],
  attachedByCampaign: Record<string, string[]>,
  campaignId: string,
): LibraryCreative[] {
  const attached = new Set(attachedByCampaign[campaignId] ?? [])
  return creatives.filter((creative) => attached.has(creative.id))
}

export function selectCreativeStats(creatives: LibraryCreative[], ads: Ad[]): CreativeStats {
  const usedIds = new Set(ads.map((ad) => ad.creativeId))
  return {
    total: creatives.length,
    used: creatives.filter((creative) => usedIds.has(creative.id)).length,
    unused: creatives.filter((creative) => !usedIds.has(creative.id)).length,
    bySource: {
      ai: creatives.filter((creative) => creative.source === 'ai').length,
      upload: creatives.filter((creative) => creative.source === 'upload').length,
      kol: creatives.filter((creative) => creative.source === 'kol').length,
    },
    byKind: {
      video: creatives.filter((creative) => creative.kind === 'video').length,
      image: creatives.filter((creative) => creative.kind === 'image').length,
    },
  }
}

export function selectAiProgress(creatives: LibraryCreative[]): AiProgress {
  const aiCreatives = creatives.filter((creative) => creative.source === 'ai')
  const videos = aiCreatives.filter((creative) => creative.kind === 'video').length
  const images = aiCreatives.length - videos
  return { made: aiCreatives.length, video: [videos, aiQuota.video], image: [images, aiQuota.image] }
}

export function selectAdsByCampaign(ads: Ad[], campaignId: string): Ad[] {
  return ads.filter((ad) => ad.campaignId === campaignId)
}

export function selectAdCtr(ads: Ad[], adId: string): number | null {
  const ad = ads.find((item) => item.id === adId)
  return ad && ad.impressions > 0 ? ad.clicks / ad.impressions : null
}

export function selectComplianceCounts(violations: Violation[], risks: Risk[]): ComplianceCounts {
  return { violation: activeViolations(violations).length, risk: activeRisks(risks).length }
}

export function selectViolationsOf(violations: Violation[], campaignId: string): Violation[] {
  return activeViolations(violations).filter((violation) => violation.campaignId === campaignId)
}

export function selectViolationEffects(
  violations: Violation[],
  campaignId: string,
): Array<{ effect: string; count: number }> {
  const counts = selectViolationsOf(violations, campaignId).reduce<Map<string, number>>((result, violation) => {
    result.set(violation.effect, (result.get(violation.effect) ?? 0) + 1)
    return result
  }, new Map())
  return [...counts.entries()]
    .map(([effect, count]) => ({ effect, count }))
    .sort((left, right) => right.count - left.count || left.effect.localeCompare(right.effect, 'zh-Hans'))
}

function violationEffectTitle(violations: Violation[]): string {
  const effects = violations.reduce<Map<string, number>>((result, violation) => {
    result.set(violation.effect, (result.get(violation.effect) ?? 0) + 1)
    return result
  }, new Map())
  const distribution = [...effects.entries()]
    .map(([effect, count]) => ({ effect, count }))
    .sort((left, right) => right.count - left.count || left.effect.localeCompare(right.effect, 'zh-Hans'))
  if (distribution.length === 1) return `${violations.length} 条广告因素材违规${distribution[0]?.effect ?? '被处置'}`
  const detail = distribution.map((item) => `${item.effect} ${item.count}`).join(' · ')
  return `${violations.length} 条广告因素材违规被处置（${detail}）`
}

export function selectRisksOf(risks: Risk[], campaignId: string): Risk[] {
  return activeRisks(risks).filter((risk) => risk.campaignId === campaignId)
}

export function selectTodayMetrics(): TodayMetrics {
  return {
    date: todayFixture.date,
    syncedAt: todayFixture.syncedAt,
    spend: todayFixture.spend,
    impressions: todayFixture.impressions,
    clicks: todayFixture.clicks,
    orders: todayFixture.orders,
    revenue: todayFixture.revenue,
    ctr: todayFixture.impressions === 0 ? 0 : todayFixture.clicks / todayFixture.impressions,
    cvr: todayFixture.clicks === 0 ? 0 : todayFixture.orders / todayFixture.clicks,
    roas: todayFixture.spend === 0 ? 0 : todayFixture.revenue / todayFixture.spend,
    deltaPrev: structuredClone(todayFixture.deltaPrev),
  }
}

export function selectHealthScore(campaigns: Campaign[], violations: Violation[], risks: Risk[]): number {
  const invalidLinks = campaigns.filter((campaign) => campaign.attentionReason === 'product_link_invalid').length
  return Math.max(0, 100 - activeViolations(violations).length * 4 - activeRisks(risks).length * 2 - invalidLinks * 4)
}

export function selectTodos(campaigns: Campaign[], ads: Ad[], violations: Violation[], risks: Risk[]): TodoItem[] {
  const items: TodoItem[] = []
  const invalidLinks = campaigns.filter((campaign) => campaign.attentionReason === 'product_link_invalid')
  const currentViolations = activeViolations(violations)
  const currentRisks = activeRisks(risks)
  const overCpa = campaigns.filter((campaign) => {
    const target = cpaTargets[campaign.id]
    return target !== undefined && campaign.metrics.cpa !== null && campaign.metrics.cpa > target * 1.3
  })
  const lowCtr = ads.filter((ad) => ad.impressions > 0 && ad.clicks / ad.impressions < accountCtr * 0.7)

  if (invalidLinks.length > 0) items.push({ id: 'broken-link', priority: 'high', icon: 'alert', title: `${invalidLinks.length} 个广告任务因商品链接失效已停投`, advice: '更新商品链接后可立即恢复投放', linkTo: `/monitor/${invalidLinks[0]?.id ?? ''}`, impact: invalidLinks.length })
  if (currentViolations.length > 0) items.push({ id: 'violations', priority: 'high', icon: 'clipboard', title: violationEffectTitle(currentViolations), advice: '更换素材或提交申诉，这部分预算已停止消耗', linkTo: '/monitor?tab=violation', impact: currentViolations.length })
  if (overCpa.length > 0) items.push({ id: 'cpa', priority: 'mid', icon: 'dollar', title: `${overCpa.length} 个广告任务的每单成本高于目标 30%`, advice: '优化受众或调整出价', linkTo: '/analytics', impact: overCpa.length })
  if (currentRisks.length > 0) items.push({ id: 'risks', priority: 'mid', icon: 'check-circle', title: `${currentRisks.length} 项合规风险待处理`, advice: currentRisks.map((risk) => risk.type === 'banned_word' ? '高危词' : risk.type === 'duplicate' ? '素材重复度过高' : '达人授权即将到期').join(' · '), linkTo: '/monitor?tab=risk', impact: currentRisks.length })
  if (lowCtr.length > 0) items.push({ id: 'low-ctr', priority: 'mid', icon: 'sparkle', title: `${lowCtr.length} 条素材的点击率低于账户基准`, advice: '用 AI 生成新素材做一轮测试', linkTo: '/creatives', impact: lowCtr.length })
  items.push({ id: 'seasonal', priority: 'low', icon: 'users', title: '季节性机会：母亲节相关受众增长中', advice: '可提前布局相关商品广告', linkTo: '/create', impact: 1 })
  return items
}

export function selectTrendMini(): TrendMini {
  return structuredClone(trendFixture)
}

function allocateInteger(total: number, weights: number[]): number[] {
  const weightTotal = weights.reduce((sum, value) => sum + value, 0)
  if (weightTotal === 0) return weights.map(() => 0)
  const raw = weights.map((weight) => total * weight / weightTotal)
  const result = raw.map(Math.floor)
  const order = raw.map((value, index) => ({ index, remainder: value - (result[index] ?? 0) })).sort((a, b) => b.remainder - a.remainder)
  const remainder = total - result.reduce((sum, value) => sum + value, 0)
  for (let index = 0; index < remainder; index += 1) {
    const target = order[index % order.length]?.index
    if (target !== undefined) result[target] = (result[target] ?? 0) + 1
  }
  return result
}

export function selectProductLens(
  campaigns: Campaign[],
  ads: Ad[],
  selectedByCampaign: Record<string, string[]>,
  rangeKey: AnalyticsRangeKey,
  lens: AnalyticsLens,
): ProductLensView {
  const range = analyticsFixture.ranges[rangeKey]
  const slice = range[lens]
  const kpis = Object.fromEntries(slice.kpis.map((item) => [item.key, item.value]))
  const table = slice.table
  const weights = table.map((row) => Number(row.spend ?? 0))
  const spends = allocateInteger(Number(kpis.spend ?? 0), weights)
  const units = lens === 'sales' ? allocateInteger(Number(kpis.orders ?? 0), table.map((row) => Number(row.orders ?? 0))) : table.map(() => 0)
  const reaches = lens === 'awareness' ? allocateInteger(Number(kpis.reach ?? 0), table.map((row) => Number(row.reach ?? 0))) : table.map((row) => Number(row.reach ?? 0))
  const impressions = lens === 'awareness' ? allocateInteger(Number(kpis.impressions ?? 0), table.map((row) => Number(row.impressions ?? 0))) : table.map((row) => Number(row.impressions ?? 0))
  const revenues = table.map((row, index) => Math.round((spends[index] ?? 0) * Number(row.roas ?? 0)))
  if (lens === 'sales' && revenues.length > 0) {
    const revenueTotal = Number(kpis.revenue ?? 0)
    const largest = revenues.reduce((best, value, index) => value > (revenues[best] ?? 0) ? index : best, 0)
    revenues[largest] = (revenues[largest] ?? 0) + revenueTotal - revenues.reduce((sum, value) => sum + value, 0)
  }

  const rows = table.map<ProductLensRow>((row, index) => {
    const campaignId = String(row.campaignId)
    const campaign = campaigns.find((item) => item.id === campaignId)
    const product = campaign ? productsById.get(campaign.productId) : undefined
    const spend = spends[index] ?? 0
    const revenue = lens === 'sales' ? (revenues[index] ?? 0) : 0
    const rowUnits = units[index] ?? 0
    const reach = reaches[index] ?? 0
    const impressionsValue = impressions[index] ?? 0
    return {
      campaignId,
      productId: product?.id ?? '',
      product: product?.name ?? String(row.label ?? ''),
      image: product?.image ?? '',
      linkOk: campaign?.attentionReason !== 'product_link_invalid',
      ads: selectedByCampaign[campaignId]?.length ?? campaign?.adCount ?? 0,
      adsLive: ads.filter((ad) => ad.campaignId === campaignId && ad.status === 'live').length,
      units: rowUnits,
      aov: rowUnits > 0 ? revenue / rowUnits : null,
      revenue,
      spend,
      acos: revenue > 0 ? spend / revenue : null,
      reach,
      impressions: impressionsValue,
      visits: campaign?.resultMetric === 'visits' ? campaign.result.current : null,
      cpm: reach > 0 ? spend / reach * 1000 : null,
      deltaPrev: typeof row.deltaPrev === 'number' ? row.deltaPrev : null,
    }
  })
  rows.sort((a, b) => lens === 'sales' ? b.revenue - a.revenue : b.reach - a.reach)
  return {
    rangeKey,
    lens,
    rows,
    totals: {
      spend: rows.reduce((sum, row) => sum + row.spend, 0),
      units: rows.reduce((sum, row) => sum + row.units, 0),
      revenue: rows.reduce((sum, row) => sum + row.revenue, 0),
      reach: rows.reduce((sum, row) => sum + row.reach, 0),
      impressions: rows.reduce((sum, row) => sum + row.impressions, 0),
    },
  }
}

function analyticsViewsConsistent(campaigns: Campaign[]): boolean {
  const ranges: AnalyticsRangeKey[] = ['7d', '14d', '30d', 'month']
  const lenses: AnalyticsLens[] = ['sales', 'awareness']
  return ranges.every((rangeKey) => lenses.every((lens) => {
    const view = selectAnalyticsView(campaigns, rangeKey, lens, false)
    const productView = selectProductLens(campaigns, initialAds, {}, rangeKey, lens)
    const products = new Map(productView.rows.map((row) => [row.campaignId, row]))
    const kpis = Object.fromEntries(view.kpis.map((item) => [item.key, Number(item.value)]))
    if (productView.totals.spend !== Number(kpis.spend ?? 0)) return false
    if (lens === 'sales') {
      const expectedOrders = view.table.reduce((sum, row) => sum + Number(row.orders ?? 0), 0)
      const expectedRevenue = view.table.reduce((sum, row) => sum + Math.round(Number(row.spend ?? 0) * Number(row.roas ?? 0)), 0)
      if (expectedOrders !== Number(kpis.orders ?? 0) || expectedRevenue !== Number(kpis.revenue ?? 0)) return false
      if (productView.totals.units !== expectedOrders || productView.totals.revenue !== expectedRevenue) return false
      return view.table.every((row) => {
        const product = products.get(String(row.campaignId))
        return product?.spend === Number(row.spend ?? 0)
          && product.units === Number(row.orders ?? 0)
          && product.revenue === Math.round(Number(row.spend ?? 0) * Number(row.roas ?? 0))
      })
    }
    const expectedReach = view.table.reduce((sum, row) => sum + Number(row.reach ?? 0), 0)
    const expectedImpressions = view.table.reduce((sum, row) => sum + Number(row.impressions ?? 0), 0)
    if (expectedReach !== Number(kpis.reach ?? 0) || expectedImpressions !== Number(kpis.impressions ?? 0)) return false
    if (productView.totals.reach !== expectedReach || productView.totals.impressions !== expectedImpressions) return false
    return view.table.every((row) => {
      const product = products.get(String(row.campaignId))
      return product?.spend === Number(row.spend ?? 0)
        && product.reach === Number(row.reach ?? 0)
        && product.impressions === Number(row.impressions ?? 0)
    })
  }))
}

export function useCreativeLibrary(filter: CreativeFilter = {}): CreativeLibraryItem[] {
  const creatives = useDemoStore((state) => state.creatives)
  const ads = useDemoStore((state) => state.ads)
  const campaigns = useDemoStore((state) => state.campaigns)
  const violations = useDemoStore((state) => state.violations)
  const risks = useDemoStore((state) => state.risks)
  return useMemo(() => selectCreativeLibrary(creatives, ads, campaigns, violations, risks, filter), [ads, campaigns, creatives, filter, risks, violations])
}

export function useAttachedCreatives(campaignId: string): LibraryCreative[] {
  const creatives = useDemoStore((state) => state.creatives)
  const attached = useDemoStore((state) => state.attachedByCampaign)
  return useMemo(() => selectAttachedCreatives(creatives, attached, campaignId), [attached, campaignId, creatives])
}

export function useCreativeStats(): CreativeStats {
  const creatives = useDemoStore((state) => state.creatives)
  const ads = useDemoStore((state) => state.ads)
  return useMemo(() => selectCreativeStats(creatives, ads), [ads, creatives])
}

export function useAiProgress(): AiProgress {
  const creatives = useDemoStore((state) => state.creatives)
  return useMemo(() => selectAiProgress(creatives), [creatives])
}

export function useAdsByCampaign(campaignId: string): Ad[] {
  const ads = useDemoStore((state) => state.ads)
  return useMemo(() => selectAdsByCampaign(ads, campaignId), [ads, campaignId])
}

export function useAdCtr(adId: string): number | null {
  const ads = useDemoStore((state) => state.ads)
  return useMemo(() => selectAdCtr(ads, adId), [adId, ads])
}

export function useComplianceCounts(): ComplianceCounts {
  const violations = useDemoStore((state) => state.violations)
  const risks = useDemoStore((state) => state.risks)
  return useMemo(() => selectComplianceCounts(violations, risks), [risks, violations])
}

export function useViolationsOf(campaignId: string): Violation[] {
  const violations = useDemoStore((state) => state.violations)
  return useMemo(() => selectViolationsOf(violations, campaignId), [campaignId, violations])
}

export function useRisksOf(campaignId: string): Risk[] {
  const risks = useDemoStore((state) => state.risks)
  return useMemo(() => selectRisksOf(risks, campaignId), [campaignId, risks])
}

export function useTodayMetrics(): TodayMetrics {
  return selectTodayMetrics()
}

export function useTodos(): TodoItem[] {
  const campaigns = useDemoStore((state) => state.campaigns)
  const ads = useDemoStore((state) => state.ads)
  const violations = useDemoStore((state) => state.violations)
  const risks = useDemoStore((state) => state.risks)
  return useMemo(() => selectTodos(campaigns, ads, violations, risks), [ads, campaigns, risks, violations])
}

export function useHealthScore(): number {
  const campaigns = useDemoStore((state) => state.campaigns)
  const violations = useDemoStore((state) => state.violations)
  const risks = useDemoStore((state) => state.risks)
  return useMemo(() => selectHealthScore(campaigns, violations, risks), [campaigns, risks, violations])
}

export function useTrendMini(): TrendMini {
  return selectTrendMini()
}

export function useProductLens(rangeKey: AnalyticsRangeKey, lens: AnalyticsLens): ProductLensView {
  const campaigns = useDemoStore((state) => state.campaigns)
  const ads = useDemoStore((state) => state.ads)
  const selected = useDemoStore((state) => state.selectedByCampaign)
  return useMemo(() => selectProductLens(campaigns, ads, selected, rangeKey, lens), [ads, campaigns, lens, rangeKey, selected])
}

export function runW2FixtureSelfCheck(): FixtureCheck[] {
  const campaigns = campaignFixture.campaigns
  const selected = initialAds.reduce<Record<string, string[]>>((result, ad) => ({ ...result, [ad.campaignId]: [...(result[ad.campaignId] ?? []), ad.creativeId] }), {})
  const stats = selectCreativeStats(initialCreatives, initialAds)
  const lens = selectProductLens(campaigns, initialAds, selected, '7d', 'sales')
  return [
    check('第二波素材条数', initialCreatives.length, 24),
    check('第二波素材使用状态', [stats.used, stats.unused], [15, 9]),
    check('第二波 AI 进度', selectAiProgress(initialCreatives), { made: 14, video: [10, 18], image: [4, 12] }),
    check('第二波广告条数', initialAds.length, 15),
    check('第二波广告合计', validateAdSums(initialAds, campaigns), true),
    check('第二波合规计数', selectComplianceCounts(initialViolations, initialRisks), { violation: 2, risk: 3 }),
    check('第二波健康度', selectHealthScore(campaigns, initialViolations, initialRisks), 82),
    check('第二波待办', selectTodos(campaigns, initialAds, initialViolations, initialRisks).length, 6),
    check('第二波商品视角合计', [lens.totals.spend, lens.totals.units, lens.totals.revenue], [18420, 342, 52464]),
  ]
}

interface W3UpliftFixture {
  items: Uplift[]
  account: AccountUplift
}

const w3Uplift = upliftJson as unknown as W3UpliftFixture

export function selectAutopilot(campaignId: string): AutopilotState | null {
  return useDemoStore.getState().autopilotByCampaign[campaignId] ?? null
}

export function selectAutopilotEligible(campaignId: string): { ok: boolean; tip?: string } {
  const campaign = useDemoStore.getState().campaigns.find((item) => item.id === campaignId)
  return isAutopilotEligible(campaign)
}

export function selectAutopilotDefaults(campaignId: string): { goal: AutopilotState['goal']; target: number } | null {
  const campaign = useDemoStore.getState().campaigns.find((item) => item.id === campaignId)
  return campaign ? deriveAutopilotDefaults(campaign) : null
}

export function selectOptimizations(campaignId: string): Optimization[] {
  return useDemoStore.getState().optimizations
    .filter((item) => item.campaignId === campaignId)
    .sort((a, b) => b.at.localeCompare(a.at))
}

export function selectUplift(campaignId: string): Uplift | null {
  const uplift = w3Uplift.items.find((item) => item.campaignId === campaignId)
  return uplift ? structuredClone(uplift) : null
}

export function selectAccountUplift(): AccountUplift {
  const state = useDemoStore.getState()
  return {
    ...structuredClone(w3Uplift.account),
    tracked: Object.values(state.autopilotByCampaign).filter((item) => item.on).length,
    total: state.campaigns.length,
    weekRuns: state.optimizations.length,
  }
}

export function selectTrackedCampaigns(): TrackedCampaign[] {
  const state = useDemoStore.getState()
  return Object.entries(state.autopilotByCampaign)
    .filter(([, autopilot]) => autopilot.on)
    .flatMap(([campaignId]) => {
      const campaign = state.campaigns.find((item) => item.id === campaignId)
      if (!campaign) return []
      const items = state.optimizations.filter((item) => item.campaignId === campaignId)
      const lastAction = [...items].reverse().find((item) => !item.undone)?.action ?? null
      return [{ campaign, runs: items.length, lastAction }]
    })
    .sort((a, b) => b.runs - a.runs)
}

function todoRecord(item: TodoItem, records: Optimization[]): Optimization | undefined {
  const matches = records.filter((record) => {
    if (item.id === 'violations') return record.type === 'compliance' && record.reasonKey.startsWith('violation:')
    if (item.id === 'cpa') return record.type === 'audience'
    if (item.id === 'risks') return record.payload?.kind === 'risk'
    if (item.id === 'low-ctr') return record.type === 'creative' && record.reasonKey.startsWith('low-ctr:')
    return false
  })
  return matches.at(-1)
}

export function selectTodoSegments(): TodoSegments {
  const state = useDemoStore.getState()
  const todos = selectTodos(state.campaigns, state.ads, state.violations, state.risks)
  const activeCampaignIds = new Set(Object.entries(state.autopilotByCampaign).filter(([, item]) => item.on).map(([campaignId]) => campaignId))
  const executed = state.optimizations.filter((item) => !item.seeded && !item.undone && activeCampaignIds.has(item.campaignId))
  const mine: TodoItem[] = []
  const handled: HandledTodo[] = []
  const chances: TodoItem[] = []
  for (const item of todos) {
    if (item.id === 'seasonal') {
      chances.push(item)
      continue
    }
    const record = todoRecord(item, executed)
    if (!record) {
      mine.push(item)
      continue
    }
    handled.push({
      ...item,
      campaignId: record.campaignId,
      didWhat: record.action,
      linkTo: `/monitor/${record.campaignId}?focus=autopilot`,
    })
  }
  return { mine, handled, chances }
}

interface ReasoningFixture {
  byPrefix: Record<string, Reasoning>
  byKey: Record<string, Reasoning>
  noReason: string[]
}

interface ScopeFixture { items: ScopeDimension[] }
interface GeneratedFixture { byCampaign: Record<string, Generated> }
interface W4CopyFixture { scopeTab: { none: string } }

const reasoningFixture = reasoningJson as unknown as ReasoningFixture
const scopeFixture = scopeJson as unknown as ScopeFixture
const generatedFixture = generatedJson as unknown as GeneratedFixture
const copyW4 = copyW4Json as unknown as W4CopyFixture

export function selectReasoning(item: Optimization): Reasoning | null {
  const prefix = item.reasonKey.split(':')[0] ?? ''
  if (item.type === 'needs_human' || reasoningFixture.noReason.includes(prefix)) return null
  const seededPrefix = item.type === 'creative' ? 'low-ctr' : item.type
  const result = reasoningFixture.byKey[item.reasonKey]
    ?? reasoningFixture.byPrefix[prefix]
    ?? (item.campaignId === 'c-mug-us' && prefix === 'seeded' ? reasoningFixture.byPrefix[seededPrefix] : undefined)
  return result ? structuredClone(result) : null
}

export function selectAutopilotScope(campaignId: string): ScopeDimension[] {
  if (campaignId === 'c-mug-us') return structuredClone(scopeFixture.items)
  const state = useDemoStore.getState()
  if (!state.autopilotByCampaign[campaignId]) return []
  const records = state.optimizations
    .filter((item) => item.campaignId === campaignId && !item.undone)
    .sort((a, b) => b.at.localeCompare(a.at))
  if (records.length === 0) return []
  return scopeFixture.items.map((dimension) => {
    const record = records.find((item) => {
      if (dimension.key === 'creative') return item.type === 'creative'
      if (dimension.key === 'audience') return item.type === 'audience'
      if (dimension.key === 'copy') return item.type === 'strategy' || item.type === 'product'
      if (dimension.key === 'compliance') return item.type === 'compliance'
      return false
    })
    if (!record) return {
      ...structuredClone(dimension),
      state: '监控中',
      lastAction: copyW4.scopeTab.none,
      yieldLabel: '未触发',
      tone: 'idle' as const,
    }
    return {
      ...structuredClone(dimension),
      state: '已调整',
      lastAction: record.action,
      yieldLabel: record.type === 'compliance' ? '1 次' : '本轮已优化',
      tone: record.type === 'compliance' ? 'warn' as const : 'good' as const,
    }
  })
}

export function selectGenerated(campaignId: string): Generated {
  const generated = generatedFixture.byCampaign[campaignId]
  if (!generated) return { total: 0, used: 0, branches: [], headlines: [] }
  // 「已采用」现算：当前在投的素材 ∩ 本树上已进素材库的 AI 素材。
  // 未入库的候选（creativeId === null）永远不算已采用；非 AI 生成的在投素材本就不上树。
  const selected = new Set(useDemoStore.getState().selectedByCampaign[campaignId] ?? [])
  const draft = structuredClone(generated)
  let used = 0
  for (const branch of draft.branches) {
    for (const variant of branch.variants) {
      variant.used = variant.creativeId !== null && selected.has(variant.creativeId)
      if (variant.used) used += 1
    }
  }
  draft.used = used
  return draft
}
